import { FormatHelper, ModelHelper } from '@adp-wfn/mdf-core';
import { clone, isNil } from 'ramda';
import React, { Fragment, useReducer } from 'react';
import { MDFButton } from './MDFButton';
import { renderField } from './MetaFields';
import { Field, FieldChangeEvent } from './MetaFields/types';

function AnswerField({ item, onChange }) {
  const handleFieldChange = (fieldParam: Field, event: FieldChangeEvent) => {
    if (['OneOf', 'SomeOf'].includes(fieldParam.type)) {
      // Update selected flags for option-based fields
      if (fieldParam.type === 'OneOf') {
        item.data.forEach((opt) => opt.selected = opt.value === event.value);
      }
      else {
        item.data.forEach((opt) => opt.selected = Array.isArray(event.value) && event.value.includes(opt.value));
      }
    }
    // Update item data for Interview compatibility
    item.data.value = event.value;

    if (onChange) {
      onChange(item);
    }
  };

  if (isNil(item.originalData)) {
    item.originalData = clone(item.data);
  }

  // Transform Interview data to Field format
  const field: Field = {
    id: item.id,
    type: item.type === 'Text' ? 'String' : item.type,
    data: item.type === 'OneOf' || item.type === 'SomeOf' ? {
      label: '',
      options: item.data.map((opt) => ({ id: opt.id, label: opt.text, value: opt.value })),
      value: item.type === 'OneOf'
        ? item.data.find((opt) => opt.selected)?.value || null
        : item.data.filter((opt) => opt.selected).map((opt) => opt.value)
    } : {
      label: '',
      value: item.data.value
    },
    isHorizontal: item.isHorizontal,
    renderPreference: item.renderPreference,
    doubleDare: item.doubleDare,
    onChange: () => {} // Will be overridden by renderField
  };

  return renderField(field, handleFieldChange);
}

function renderAnswers(state, answer, onChange) {
  const answers = Object.keys(answer);

  return (
    <Fragment>
      {
        answers.map((answerId) => {
          const item = answer[answerId];
          item.id = answerId;

          let shouldRender = item.render !== false;

          if (item.render) {
            // Render functions need to start with ':quote' so that they aren't interpreted until now.
            // Use slice(1) to remove ':quote' and then run the script.
            shouldRender = ModelHelper.resolve(item.render.slice(1), state, 'InterviewSection');
          }

          if (shouldRender) {
            return <AnswerField key={answerId} item={item} onChange={onChange} />;
          }
          else {
            return undefined;
          }
        })
      }
    </Fragment>
  );
}

function updateAnswer(answer) {
  if (!answer.showDoubleDare) {
    answer.originalData = undefined;
  }

  return answer;
}

function interviewReducer(state, action) {
  const newState: any = { ...state };

  switch (action.type) {
    case 'goToPage':
      newState.currentPage = action.currentPage;

      if (newState.onInterviewNavigate) {
        newState.onInterviewNavigate(newState, state.currentPage);
      }

      break;

    case 'updateAnswer':
      newState.questions[action.questionId].answer[action.answer.id] = updateAnswer(action.answer);

      if (newState.onInterviewChange) {
        newState.onInterviewChange(newState);
      }

      break;
  }

  return newState;
}

function renderQuestions(questions, title, state, dispatch) {
  return (
    <Fragment>
      { title && <h4>{title}</h4> }
      { questions.map((questionId) => {
        const question = state.questions[questionId];
        let shouldRender = true;

        if (question.render) {
          // Render functions need to start with ':quote' so that they aren't interpreted until now.
          // Use slice(1) to remove ':quote' and then run the script.
          shouldRender = ModelHelper.resolve(question.render.slice(1), state, 'InterviewSection');
        }

        if (shouldRender) {
          return (
            <div key={questionId} className={'mdf-interview-queston'} style={{ margin: '5px' }}>
              <div>{question.text}</div>
              {
                renderAnswers(state, question.answer, (answer) => {
                  dispatch({ type: 'updateAnswer', questionId, answer });
                })
              }
            </div>
          );
        }
        else {
          return undefined;
        }
      })}
    </Fragment>
  );
}

export function InterviewSection(props: any) {
  const state = props.state;
  const dispatch = props.dispatch;

  const currentPage = props.currentPage || state.questionFlow[state.currentPage];

  return (
    <div className={'mdf-interview-section'} style={{ margin: '5px' }}>
      { renderQuestions(currentPage.questions, currentPage.title, state, dispatch) }
    </div>
  );
}

function renderPages(pages, state, dispatch) {
  return (
    <Fragment>
      { pages.map((page, index) => <InterviewSection key={`is-${index}`} currentPage={state.questionFlow[page]} state={state} dispatch={dispatch}/>) }
    </Fragment>
  );
}

export function InterviewSummary(props: any) {
  const state = props.state;
  const dispatch = props.dispatch;

  const currentPage = state.questionFlow[state.currentPage];

  return (
    <div>
      { renderPages(currentPage.pages, state, dispatch) }
    </div>
  );
}

export function Interview(props: any) {
  const [ state, dispatch ] = useReducer(interviewReducer, props);

  if (!state.currentPage) {
    dispatch({ type: 'goToPage', currentPage: state.questionFlow.startAt || state.questionFlow[Object.keys(state.questionFlow)[0]].id });
    return;
  }

  const currentPage = state.questionFlow[state.currentPage];
  let Component;
  let isSummary = false;

  if (currentPage.questions) {
    Component = InterviewSection;
  }
  else {
    isSummary = true;
    Component = InterviewSummary;
  }

  const summaryButton = (
    <MDFButton
      buttonStyle="primary"
      onClick={() => {
        if (props.onInterviewDone) {
          props.onInterviewDone(state);
        }
      }}
    >
      {FormatHelper.formatMessage('@@Done')}
    </MDFButton>
  );

  const saveButton = (
    <MDFButton
      buttonStyle="link"
      onClick={() => {
        if (props.onInterviewSaveAndReturn) {
          props.onInterviewSaveAndReturn(state);
        }
      }}
    >
      {FormatHelper.formatMessage('@@save_finish_later')}
    </MDFButton>

  );

  const previousButton = (
    <MDFButton
      buttonStyle="link"
      onClick={() => {
        dispatch({ type: 'goToPage', currentPage: currentPage.previous });
      }}
    >
      {FormatHelper.formatMessage('@@Previous')}
    </MDFButton>
  );

  const nextButton = (
    <MDFButton
      buttonStyle="link"
      onClick={() => {
        dispatch({ type: 'goToPage', currentPage: currentPage.next });
      }}
    >
      {FormatHelper.formatMessage('@@Next')}
    </MDFButton>
  );

  return (
    <Fragment>
      <Component {...props} state={state} dispatch={dispatch}/>
      {isSummary ? summaryButton : saveButton}
      {currentPage.previous && previousButton}
      {currentPage.next && nextButton}
    </Fragment>
  );
}
